import { useCallback, useEffect, useRef } from 'react';

import Icon from 'components/shared/Icon/Icon';

const ResizeGrabber = ({
  targetRef,
  disabled,
  isResize,
  isMaxChars,
  maxHeight,
  floorHeightRef,
  isPinnedRef
}) => {
  const cleanupRef = useRef(null);

  const handleMouseDown = useCallback(
    (event) => {
      const target = targetRef?.current;
      if (!target || disabled) return;

      event.preventDefault();

      const startY = event.clientY;
      const startHeight = target.offsetHeight;

      const handleMouseMove = (moveEvent) => {
        const nextHeight = startHeight + (moveEvent.clientY - startY);
        const clamped = Math.min(Math.max(nextHeight, 0), maxHeight ?? Infinity);
        target.style.height = `${clamped}px`;
        if (floorHeightRef) floorHeightRef.current = clamped;
      };

      const cleanup = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', cleanup);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        cleanupRef.current = null;
        if (isPinnedRef) isPinnedRef.current = target.scrollHeight > target.offsetHeight;
      };

      cleanupRef.current = cleanup;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ns-resize';

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', cleanup);
    },
    [targetRef, disabled, maxHeight, floorHeightRef, isPinnedRef]
  );

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  if (disabled || !isResize) return null;

  return (
    <span
      role="presentation"
      onMouseDown={handleMouseDown}
      className="absolute bottom-2 right-2 z-30 flex cursor-ns-resize select-none items-center justify-center text-neutral-500">
      <Icon
        stroke
        size={12}
        icon="re-size-filled"
        color={isMaxChars ? 'danger' : 'neutral'}
        shade={isMaxChars ? 500 : 600}
      />
    </span>
  );
};

export default ResizeGrabber;
